// Every participant estimates the server's clock so recordings started "at
// server time T" begin together and line up on the timeline. NTP-style:
// send our time, get the server's, assume the halfway point. The sample
// with the shortest round trip has the least room for error, so that's
// the one we use; a short burst on join, a few pings now and then after.

export interface Sample {
  /** server − local, ms */
  offset: number;
  rtt: number;
}

export function sampleFrom(c0: number, s: number, c1: number): Sample {
  return { offset: s - (c0 + c1) / 2, rtt: c1 - c0 };
}

export function bestSample(samples: Sample[]): Sample | null {
  return samples.reduce<Sample | null>((best, x) => (!best || x.rtt < best.rtt ? x : best), null);
}

interface Channel {
  send(msg: Record<string, unknown>): void;
  on(type: string, fn: (msg: { type: string } & Record<string, unknown>) => void): () => void;
}

export interface ClockSyncOptions {
  /** High-resolution wall clock in ms. */
  now?: () => number;
  burst?: number;
  burstGapMs?: number;
  refreshMs?: number;
  /** Samples kept (recent ones; the network changes). */
  keep?: number;
}

export const highResNow = () => performance.timeOrigin + performance.now();

export class ClockSync {
  private samples: Sample[] = [];
  private timers: Array<ReturnType<typeof setTimeout>> = [];
  private unsubscribe: (() => void) | null = null;
  private pendingReport = 0;
  private now: () => number;

  constructor(
    private channel: Channel,
    private opts: ClockSyncOptions = {},
  ) {
    this.now = opts.now ?? highResNow;
  }

  start(): void {
    this.unsubscribe = this.channel.on('time.pong', (m) => {
      if (typeof m.c0 !== 'number' || typeof m.s !== 'number') return;
      this.samples.push(sampleFrom(m.c0, m.s, this.now()));
      this.samples = this.samples.slice(-(this.opts.keep ?? 20));
      if (--this.pendingReport === 0) this.report();
    });
    this.burst(this.opts.burst ?? 8);
    const refresh = setInterval(() => this.burst(3), this.opts.refreshMs ?? 30_000);
    this.timers.push(refresh as unknown as ReturnType<typeof setTimeout>);
  }

  private burst(n: number) {
    this.pendingReport = n;
    for (let i = 0; i < n; i++) {
      this.timers.push(setTimeout(() => this.channel.send({ type: 'time.ping', c0: this.now() }), i * (this.opts.burstGapMs ?? 100)));
    }
  }

  private report() {
    const best = bestSample(this.samples);
    if (best) this.channel.send({ type: 'clock.report', offsetMs: Math.round(best.offset * 10) / 10, rttMs: Math.round(best.rtt * 10) / 10 });
  }

  /** server − local in ms (0 until measured). */
  get offset(): number {
    return bestSample(this.samples)?.offset ?? 0;
  }

  get rtt(): number | null {
    return bestSample(this.samples)?.rtt ?? null;
  }

  get synced(): boolean {
    return this.samples.length > 0;
  }

  /** The server's clock, as best we can tell. */
  serverNow(): number {
    return this.now() + this.offset;
  }

  /** Local high-res time at which the server clock reads `serverMs`. */
  localAt(serverMs: number): number {
    return serverMs - this.offset;
  }

  stop(): void {
    for (const t of this.timers) {
      clearTimeout(t);
      clearInterval(t);
    }
    this.timers = [];
    this.unsubscribe?.();
  }
}
